// Shared helpers and small UI pieces for the admin console.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, X } from "lucide-react";

/* ---------------- formatting ---------------- */

export const errorText = (err, fallback = "Something went wrong.") => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  // FastAPI validation errors come back as a list of {msg}
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => d?.msg || String(d)).join("; ");
  }
  if (!err?.response && err?.message === "Network Error") {
    return "Can't reach the server. Check your connection and try again.";
  }
  return fallback;
};

// Error bodies for responseType:"blob" requests arrive as a Blob.
export const blobErrorText = async (err, fallback) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (typeof parsed?.detail === "string") return parsed.detail;
    } catch {
      /* not JSON */
    }
    return fallback;
  }
  return errorText(err, fallback);
};

const toDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDate = (value) => {
  const d = toDate(value);
  return d ? d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";
};

export const formatDateTime = (value) => {
  const d = toDate(value);
  return d
    ? d.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
};

export const timeAgo = (value) => {
  const d = toDate(value);
  if (!d) return "";
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 45) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(value);
};

export const fullName = (u) =>
  [u?.name, u?.surname].filter(Boolean).join(" ") || u?.username || u?.email || "Unnamed user";

export const initials = (u) =>
  `${u?.name?.[0] || ""}${u?.surname?.[0] || ""}`.toUpperCase() ||
  (u?.username?.[0] || u?.email?.[0] || "?").toUpperCase();

export const formatScore = (value) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? "—"
    : `${Math.round(Number(value) * 10) / 10}%`;

export const cleanParams = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined)
  );

/* ---------------- toasts ---------------- */

const ToastContext = createContext({ notify: () => {} });
export const useToast = () => useContext(ToastContext);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const notify = useCallback(
    (message, type = "success") => {
      const id = `${Date.now()}-${Math.random()}`;
      setToasts((t) => [...t.slice(-3), { id, message, type }]);
      setTimeout(() => dismiss(id), 4500);
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={{ notify }}>
      {children}
      {createPortal(
      <div className="admin-toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`admin-toast admin-toast-${t.type}`}>
            {t.type === "error" ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
            <span>{t.message}</span>
            <button type="button" onClick={() => dismiss(t.id)} aria-label="Dismiss notification">
              <X size={16} />
            </button>
          </div>
        ))}
      </div>,
      document.body
      )}
    </ToastContext.Provider>
  );
};

/* ---------------- dialogs (modal + drawer) ---------------- */

// Only the top-most open dialog reacts to Escape.
const dialogStack = [];

export const Dialog = ({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  variant = "modal",
  size = "md",
}) => {
  const titleId = useId();
  const panelRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    dialogStack.push(token);
    const previouslyFocused = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape" && dialogStack[dialogStack.length - 1] === token) {
        e.stopPropagation();
        closeRef.current?.();
      }
    };
    document.addEventListener("keydown", onKey);

    const focusTimer = setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      // Drawers start at the top; modals jump to their first field.
      const target =
        panel.querySelector("[data-autofocus]") ||
        (variant === "modal" && panel.querySelector("input:not([disabled]), select, textarea")) ||
        panel;
      target.focus();
    }, 20);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKey);
      const idx = dialogStack.indexOf(token);
      if (idx >= 0) dialogStack.splice(idx, 1);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
      }
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  // Portal to <body> so no transformed ancestor can trap position: fixed
  return createPortal(
    <div
      className={`admin-overlay admin-overlay-${variant}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeRef.current?.();
      }}
    >
      <div
        ref={panelRef}
        className={`admin-dialog admin-${variant} admin-dialog-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="admin-dialog-head">
          <div>
            <h3 id={titleId}>{title}</h3>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button
            type="button"
            className="admin-icon-btn"
            onClick={() => closeRef.current?.()}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="admin-dialog-body">{children}</div>
        {footer && <div className="admin-dialog-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

/**
 * Confirmation for destructive actions.
 * `requireText` forces the admin to type an exact value (e.g. the user's email).
 */
export const ConfirmDialog = ({
  open,
  title,
  children,
  confirmLabel = "Delete",
  busy = false,
  error = null,
  requireText = null,
  requireLabel,
  onConfirm,
  onCancel,
}) => {
  const [typed, setTyped] = useState("");
  const inputId = useId();

  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const matches = !requireText || typed.trim().toLowerCase() === requireText.trim().toLowerCase();

  return (
    <Dialog
      open={open}
      title={title}
      onClose={busy ? () => {} : onCancel}
      size="sm"
      footer={
        <>
          <button type="button" className="eg-btn eg-btn-outline" onClick={onCancel} disabled={busy} data-autofocus={requireText ? undefined : true}>
            Cancel
          </button>
          <button
            type="button"
            className="eg-btn admin-btn-danger"
            onClick={onConfirm}
            disabled={busy || !matches}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </>
      }
    >
      <div className="admin-confirm">
        <div className="admin-confirm-icon">
          <AlertTriangle size={22} />
        </div>
        <div className="admin-confirm-text">{children}</div>
      </div>
      {requireText && (
        <form
          className="eg-field admin-confirm-field"
          onSubmit={(e) => {
            e.preventDefault();
            if (matches && !busy) onConfirm();
          }}
        >
          <label className="eg-label" htmlFor={inputId}>
            {requireLabel || (
              <>
                Type <code>{requireText}</code> to confirm
              </>
            )}
          </label>
          <input
            id={inputId}
            className="eg-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            data-autofocus
          />
        </form>
      )}
      {error && <div className="admin-error admin-error-tight">{error}</div>}
    </Dialog>
  );
};

/* ---------------- misc ---------------- */

export const Pagination = ({ page, pages, total, noun = "item", plural, onPage, disabled }) => (
  <div className="admin-pagination">
    <span>
      {total ?? 0} {total === 1 ? noun : plural || `${noun}s`}
    </span>
    <div>
      <button
        type="button"
        onClick={() => onPage(page - 1)}
        disabled={disabled || page <= 1}
        aria-label="Previous page"
      >
        <ChevronLeft size={18} />
      </button>
      <span>
        Page {page} of {Math.max(pages || 1, 1)}
      </span>
      <button
        type="button"
        onClick={() => onPage(page + 1)}
        disabled={disabled || page >= (pages || 1)}
        aria-label="Next page"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  </div>
);

export const EmptyState = ({ icon: Icon, title, children, action }) => (
  <div className="admin-empty">
    {Icon && (
      <div className="admin-empty-icon">
        <Icon size={24} />
      </div>
    )}
    <strong>{title}</strong>
    {children && <p>{children}</p>}
    {action}
  </div>
);

export const Loading = ({ label = "Loading…" }) => (
  <div className="admin-loading" role="status">
    <span className="spinner" aria-hidden="true" />
    <span>{label}</span>
  </div>
);

export const Switch = ({ checked, onChange, disabled, label, id }) => (
  <button
    id={id}
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className={`admin-switch ${checked ? "on" : ""}`}
    onClick={() => onChange(!checked)}
    disabled={disabled}
  >
    <span />
  </button>
);

export const RoleBadge = ({ role }) => {
  const label = role ? role[0].toUpperCase() + role.slice(1) : "—";
  const cls =
    role === "admin" ? "admin-badge-gold" : role === "teacher" ? "admin-badge-info" : "admin-badge-neutral";
  return <span className={`admin-badge ${cls}`}>{label}</span>;
};

export const UserStatusBadges = ({ user }) => (
  <div className="admin-badges">
    {user.is_verified ? (
      <span className="admin-badge admin-badge-success">Verified</span>
    ) : (
      <span className="admin-badge admin-badge-warn">Unverified</span>
    )}
    {user.is_suspended && <span className="admin-badge admin-badge-danger">Suspended</span>}
  </div>
);
